import * as React from 'react';
import {Redirect} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {applyFilterSubmission} from 'nutshell-core';

import {convertToArray, convertToObj} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {readLocalStorage} from '../../util/localStorage';

import {generateDefaultQueryIncludingUnknownParams} from '../reports/generate-default-query-including-unknown-params';
import {generateUrlUpdatingParam} from '../reports/generate-url-updating-param';
import {getReportDefaultState} from '../reports/report-constants';
import type {ReportKey, ReportURLParams} from '../reports/report-types';
import {resetUrlFilters} from '../reports/reset-url-filters';
import {updateUrlParam} from '../reports/update-url-param';

export function getBasePathWithoutTab(pathname: string, tab?: string | null) {
    if (!tab) return pathname;

    return pathname.split(`/${tab}`)[0];
}

export function resetPage(urlParams: any) {
    if (urlParams.list && urlParams.list.page) {
        urlParams.list.page = '1';
    }

    return urlParams;
}

export type ListPageWrapperProps = {
    routeProps: ContextRouter;
    localStorageKey: string;
    defaultQuery: string;
    ignoreUnknownParams?: boolean;
    includeHash?: boolean;
    children: (arg1: {
        urlParams: ReportURLParams<'contacts'>;
        activeFilters: FilterObject[];
        onParamChange: () => void;
        getUpdatedUrl: (arg1: any) => string;
        onUpdateFilters: (arg1: FilterObject) => void;
        onRemoveAllFilters: () => void;
    }) => React.ReactElement;
};

export function ListPageWrapper(props: ListPageWrapperProps) {
    const {routeProps, localStorageKey, defaultQuery, ignoreUnknownParams, includeHash} = props;

    // Strip trailing slash if there is one
    const currentPath = routeProps.location.pathname.endsWith('/')
        ? routeProps.location.pathname.slice(0, -1)
        : routeProps.location.pathname;

    const urlParamString = routeProps.location.search.slice(1); // Remove "?"
    const urlParams: ReportURLParams<'contacts'> = Routing.deparam(urlParamString);
    const hash = includeHash ? routeProps.location.hash : '';

    if (routeProps.location.search === '') {
        const persistedQuery = readLocalStorage(localStorageKey);
        if (persistedQuery) {
            const prefixedPersistedQuery = persistedQuery.startsWith('?')
                ? persistedQuery
                : `?${persistedQuery}`;

            return <Redirect to={`${currentPath}${prefixedPersistedQuery}${hash}`} />;
        }

        if (defaultQuery) {
            return <Redirect to={`${currentPath}${defaultQuery}${hash}`} />;
        }
    }

    if (ignoreUnknownParams) {
        const newPath = generateDefaultQueryIncludingUnknownParams(
            urlParamString,
            urlParams,
            defaultQuery
        );

        if (newPath) {
            return <Redirect to={`${currentPath}${newPath}${hash}`} />;
        }
    }

    const activeFilters = convertToArray(urlParams.filter);

    const paramChanger = updateUrlParam(
        routeProps.location.pathname,
        urlParams,
        routeProps.history.replace,
        localStorageKey
    );

    const getUpdatedUrl = generateUrlUpdatingParam(routeProps.location.pathname, urlParams);

    const handleFilterSubmit = (newFilter: FilterObject) => {
        const newFilters = applyFilterSubmission(activeFilters, newFilter);
        const newParams: Record<string, any> = {};
        newParams.filter = convertToObj(newFilters);
        paramChanger(newParams, false);
    };

    return props.children({
        activeFilters,
        urlParams,
        // @ts-expect-error
        onParamChange: paramChanger,
        getUpdatedUrl: (newParams: any) => getUpdatedUrl(newParams),
        onUpdateFilters: handleFilterSubmit,
        onRemoveAllFilters: () => {
            const updatedUrlParams = resetPage(urlParams);

            // Archiving behaves like a filter, but visually it's a tab.
            // So we should never remove it when removing all filters.
            const hasArchivedFilter = activeFilters.find(
                (filter) => Object.keys(filter)[0] === 'archived'
            );
            const defaultSearch = hasArchivedFilter ? '?filter[archived]=true' : '';

            resetUrlFilters(
                routeProps.location.pathname,
                updatedUrlParams,
                defaultSearch,
                routeProps.history.replace,
                localStorageKey
            )();
        },
    });
}

/**
 * Wrapper to share logic for url params from pathname to persist queries in local storage.
 */
export type UrlParamsProps = {
    routeProps: ContextRouter;
    reportKey?: ReportKey;
    initialFilter?: FilterObject;
    localStorageKey?: string;
    children: (arg1: {
        urlParams: ReportURLParams<'contacts'>;
        activeFilters: FilterObject[];
        onParamChange: () => void;
        onParamPush: () => void;
        onRemoveAllFilters: () => void;
    }) => React.ReactElement;
};

export function UrlParamsWrapper(props: UrlParamsProps) {
    const routeProps = props.routeProps;

    const defaultState = props.reportKey ? getReportDefaultState(props.reportKey) : undefined;
    const localStorageKey = props.localStorageKey
        ? props.localStorageKey
        : getBasePathWithoutTab(routeProps.location.pathname, routeProps.match.params.tab);

    if (routeProps.location.search === '') {
        // Strip trailing slash if there is one
        const currentPath = routeProps.location.pathname.endsWith('/')
            ? routeProps.location.pathname.slice(0, -1)
            : routeProps.location.pathname;

        const defaultQuery = defaultState ? defaultState.search : '';

        const persistedQuery = localStorageKey ? readLocalStorage(localStorageKey) : undefined;

        if (persistedQuery && persistedQuery.length > 1) {
            const prefixedPersistedQuery = persistedQuery.startsWith('?')
                ? persistedQuery
                : `?${persistedQuery}`;

            return <Redirect to={`${currentPath}${prefixedPersistedQuery}`} />;
        }

        if (defaultQuery) {
            return <Redirect to={`${currentPath}${defaultQuery}`} />;
        }
    }

    const urlParamString = routeProps.location.search.slice(1); // Remove "?"
    const urlParams: ReportURLParams<'contacts'> = Routing.deparam(urlParamString);
    const activeFilters = convertToArray(urlParams.filter);

    const paramChanger = updateUrlParam(
        routeProps.location.pathname,
        urlParams,
        routeProps.history.replace,
        localStorageKey
    );

    // Apply an initial filter behind-the-scenes
    const adjustedActiveFilters = props.initialFilter
        ? applyFilterSubmission(activeFilters, props.initialFilter)
        : activeFilters;

    return props.children({
        urlParams,
        activeFilters: adjustedActiveFilters ? adjustedActiveFilters : activeFilters,
        // @ts-expect-error
        onParamChange: paramChanger,
        // @ts-expect-error
        onParamPush: updateUrlParam(
            routeProps.location.pathname,
            urlParams,
            routeProps.history.push,
            localStorageKey
        ),
        onRemoveAllFilters: () => {
            const updatedUrlParams = resetPage(urlParams);

            resetUrlFilters(
                routeProps.location.pathname,
                updatedUrlParams,
                defaultState ? defaultState.search : '',
                routeProps.history.replace,
                localStorageKey
            )();
        },
    });
}
